/* eslint-disable @typescript-eslint/no-explicit-any */
import { useSelector, useDispatch } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { Tabs } from "@/subframe/components/Tabs";
import { Tooltip } from "@/subframe/components/Tooltip";
import { IconButton } from "@/subframe/components/IconButton";
import SubframeCore from "@subframe/core";
import { Button } from "@/subframe/components/Button";
import { TextArea } from "@/subframe/components/TextArea";
import { DropdownMenu } from "@/subframe/components/DropdownMenu";
import { RootState, AppDispatch } from "../redux/store";
import { Recipient } from "../redux/interfaces/reducersInterfaces";
import { sendTextMessage } from "../redux/reducers/messengerSlice";
import FileSelectionMenu from "./media/FileSelectionMenu";
import { addNotification } from "../redux/reducers/notificationSlice";
import { NotificationIcon, NotificationType } from "../Utils/notificationEnums";
import VoiceMessageHandler from "./media/VoiceMessageHandler";
import moment from "moment";
import ScheduleMessage from "./templates/ScheduleTemplateMenu";
import type { SchedulePayload } from "./templates/ScheduleTemplateMenu";

interface TextMessageSendProps {
  currentRecipient: Recipient | null;
  handleAiToHuman: () => void;
}

function TextMessageSend({
  currentRecipient,
  handleAiToHuman
}: TextMessageSendProps) {
  const dispatch = useDispatch<AppDispatch>();
  const system_user_id = useSelector(
    (state: RootState) => state.auth.system_user_id
  );
  const userDisplayName = useSelector(
    (state: RootState) => state.auth.userDisplayName
  );
  const [isSending, setIsSending] = useState<boolean>(false);
  const [messageText, setMessageText] = useState("");
  const [isScheduleMessagePopupOpen, setIsScheduleMessagePopupOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [messageText]);

  const handleSendMessage = async (schedulePayload?: SchedulePayload) => {
    const messageToSend = messageText.trim();
    if (!messageToSend || !system_user_id || !currentRecipient || isSending) return;

    const isScheduled = !!schedulePayload?.is_schedule;

    try {
      setIsSending(true);
      setMessageText("");
      await dispatch(
        sendTextMessage({
          text: messageToSend,
          currentRecipient,
          senderUserId: system_user_id,
          senderDisplayName: userDisplayName,
          is_schedule: isScheduled,
          ...(schedulePayload || {}),
        })
      ).unwrap();
      setIsSending(false);

      if (isScheduled) {
        let scheduleMessage = "Your message has been scheduled.";

        if (schedulePayload?.schedule_type === "specific") {
          const formattedLocalDateTime = schedulePayload.schedule_datetime_date
            ? moment(schedulePayload.schedule_datetime_date).local().format("MMM D, YYYY hh:mm A")
            : "selected date and time";
          const selectedTimezone = schedulePayload.time_zone || "your timezone";
          scheduleMessage = `Your message will be sent on ${formattedLocalDateTime} (${selectedTimezone}).`;
        } else if (schedulePayload?.schedule_type === "relative") {
          const unitValue = schedulePayload.unit_of_time_value || "";
          const unitName = schedulePayload.unit_of_time_name || "";
          scheduleMessage = `Your message will be sent after ${unitValue} ${unitName} from now.`;
        }

        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Message Scheduled",
            message: scheduleMessage,
            icon: NotificationIcon.SUCCESS,
          })
        );
      } else {
        dispatch(
          addNotification({
            id: Date.now(),
            type: NotificationType.SUCCESS,
            title: "Message Sent",
            message: "Your message has been sent successfully",
            icon: NotificationIcon.SUCCESS,
          })
        );
      }

      handleAiToHuman();
    } catch (error: any) {
      setIsSending(false);
      const errorTitle = isScheduled ? "Schedule Failed" : "Message Not Sent";
      dispatch(
        addNotification({
          id: Date.now(),
          type: NotificationType.ERROR,
          title: errorTitle,
          message: error.message || "Something went wrong. Please try again.",
          icon: NotificationIcon.ERROR,
        })
      );
    }
  };

  const handleTextScheduleFromMenu = (schedulePayload: SchedulePayload) => {
    handleSendMessage(schedulePayload);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      <div className="conveythis-no-translate flex w-full flex-col items-start gap-1 border-t border-solid border-neutral-border bg-[#f1f2f6ff] px-6 py-4">
        <div className="hidden w-full items-center gap-2">
          <Button icon="FeatherMessageSquare">Message</Button>
          <Button variant="brand-secondary" icon="FeatherLayout">
            Templates
          </Button>
        </div>

        <Tabs className="hidden">
          <Tabs.Item active={true}>Message</Tabs.Item>
          <Tabs.Item>Templates</Tabs.Item>
        </Tabs>

        <div className="flex w-full items-end gap-2 border-t border-solid border-neutral-border px-1 py-1">
          <SubframeCore.DropdownMenu.Root>
            <SubframeCore.DropdownMenu.Trigger asChild={true}>
              <IconButton
                variant="neutral-secondary"
                icon="FeatherPaperclip"
              />
            </SubframeCore.DropdownMenu.Trigger>
            <SubframeCore.DropdownMenu.Portal>
              <SubframeCore.DropdownMenu.Content
                side="bottom"
                align="start"
                sideOffset={4}
                asChild={true}
              >
                <DropdownMenu className="h-auto min-w-[208px] grow shrink-0 basis-0 self-stretch">
                  <FileSelectionMenu currentRecipient={currentRecipient}/>
                </DropdownMenu>
              </SubframeCore.DropdownMenu.Content>
            </SubframeCore.DropdownMenu.Portal>
          </SubframeCore.DropdownMenu.Root>

          <TextArea className="grow shrink-0 basis-0" label="" helpText="">
            <TextArea.Input
              ref={textareaRef}
              className="conveythis-no-translate max-h-32 overflow-y-auto w-full"
              style={{ minHeight: '32px' }}
              rows={1}
              placeholder="Type a message"
              value={messageText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setMessageText(e.target.value)
              }
              onKeyDown={handleKeyDown}
            />
          </TextArea>

          <VoiceMessageHandler currentRecipient={currentRecipient} />

          <div className="flex items-end self-end rounded-lg">
            <SubframeCore.Tooltip.Provider>
              <SubframeCore.Tooltip.Root>
                <SubframeCore.Tooltip.Trigger asChild={true}>
                  <IconButton
                    variant="brand-primary"
                    icon="FeatherCalendarClock"
                    onClick={() => setIsScheduleMessagePopupOpen(true)}
                    disabled={
                      !messageText.trim() ||
                      !system_user_id ||
                      !currentRecipient ||
                      isSending
                    }
                  />
                </SubframeCore.Tooltip.Trigger>
                <SubframeCore.Tooltip.Portal>
                  <SubframeCore.Tooltip.Content
                    side="bottom"
                    align="center"
                    sideOffset={4}
                    asChild={true}
                  >
                    <Tooltip>Schedule send</Tooltip>
                  </SubframeCore.Tooltip.Content>
                </SubframeCore.Tooltip.Portal>
              </SubframeCore.Tooltip.Root>
            </SubframeCore.Tooltip.Provider>

            <div className="flex w-px flex-none flex-col items-center gap-2 self-stretch" />

            <IconButton
              className="border-l border-brand-50"
              variant="brand-primary"
              icon="FeatherSendHorizontal"
              onClick={() => handleSendMessage()}
              disabled={
                !messageText.trim() ||
                !system_user_id ||
                !currentRecipient ||
                isSending
              }
            />
          </div>
        </div>
      </div>

      <ScheduleMessage
        open={isScheduleMessagePopupOpen}
        setIsScheduleMessagePopupOpen={setIsScheduleMessagePopupOpen}
        onScheduleSubmit={handleTextScheduleFromMenu}
      />
    </>
  );
}

export default TextMessageSend;
